import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { useStyles } from '../style'
import { OutlinedButton } from '../../../elements'
import { Grid, TextField, MenuItem, Box } from '@material-ui/core'
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from '@material-ui/pickers'
import DateFnsUtils from '@date-io/date-fns'
import _ from 'lodash'
import { useFormik } from 'formik'
import * as yup from 'yup'
import moment from 'moment'
import { reportService } from '../../../Services/ReportService/ReportService'
import { DisplayMessage, Loader } from '../../../Components'
import * as messageTypes from '../../../utils/messageTypes'
import { COMMON } from '../../../utils/constants'
import DateRangeOutlinedIcon from '@material-ui/icons/DateRangeOutlined'
import CustomDatePicker from '../../../elements/DatePicker'
import { pushToDataLayer, USERBEHAVIOURTRACKING } from '../../../utils/gaEnvironment'

const validationSchema = yup.object().shape({
  account: yup
    .string('Select account name')
    .required('Account name is required'),
  program: yup
    .string('Select program name')
    .required('Program name is required'),
  from_date: yup
    .date().typeError('Not a valid date')
    .required("Start date is required"),
  to_date: yup
    .date('Enter valid date').typeError('Not a valid date')
    .required("End date is required"),
})

function EventsSummary(props) {
  const classes = useStyles()
  const [programs, setPrograms] = useState((props.isSuperAdmin || props.isViewOnly) ? props.programs : props.programs.filter(program => program.feature_flags && program.feature_flags.program_and_events))
  const [open, setOpen] = useState()
  const [message, setMessage] = useState()
  const [messageType, setMessageType] = useState()
  const [loading, setLoading] = useState()
  const [fromDate, setFromDate] = useState(false)
  const [toDate, setToDate] = useState(false)
  const { currentUser } = props

  const formik = useFormik({
    initialValues: {
      account: (props.isSuperAdmin || props.isViewOnly) ? '' : currentUser && currentUser.account_id,
      program: '',
      from_date: null,
      to_date: null
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      pushToDataLayer({ event: USERBEHAVIOURTRACKING, accountID: currentUser.account_id, programID: values.program })
      getReport(values)
    },
  })

  const getReport = (values) => {
    const { currentUser } = props
    const data = { ...values, start_date: moment.utc(values.from_date).startOf('day').format('YYYY-MM-DD HH:mm:ssZ'), end_date: moment.utc(values.to_date).endOf('day').format('YYYY-MM-DD HH:mm:ssZ') }
    setLoading(true)
    reportService.getEventSummaryReport(currentUser, data)
      .then(reportData => {
        setLoading(false)
        setOpen(true)
        if (values.report_type === 'PDF') {
          let popUp = window.open(reportData, '_blank', '')
          if (popUp == null || typeof (popUp) == 'undefined') {
            setMessage('Please disable your pop-up blocker and download file again.')
            setMessageType(messageTypes.INFO)
          }
          else {
            setMessage('Report downloading')
            setMessageType(messageTypes.SUCCESS)
          }
        } else {
          downloadFile(reportData, values)
          setMessage('Report downloading')
          setMessageType(messageTypes.SUCCESS)
        }
      })
      .catch(error => {
        setLoading(false)
        setOpen(true)
        setMessage(_.get(error, 'data.error.displayMessage', 'Something went wrong'))
        setMessageType(messageTypes.ERROR)
      })
  }

  const downloadFile = (fileUrl, values) => {
    const a = document.createElement("a")
    a.href = fileUrl
    a.target = values.report_type === 'PDF' ? "_blank" : ''
    a.setAttribute("download", `EventSummaryReport_${new Date()}.${formik.values.report_type.toLowerCase()}`)
    a.click()
  }

  const handleAccountChange = (e) => {
    formik.setFieldValue([e.target.name], e.target.value)
    formik.setFieldValue('program', '')
    setPrograms(props.programs.filter(program => (props.isSuperAdmin || props.isViewOnly) ? program.account_id === e.target.value : program.account_id === e.target.value && program.feature_flags && program.feature_flags.program_and_events))
  }

  useEffect(() => {
    formik.resetForm()
  }, [props.name])

  return (
    <>
      <Loader loading={loading} />
      <DisplayMessage open={open} message={message} messageType={messageType} handleClose={() => setOpen(false)} />
      <div className={classes.formHeading}>Events Summary</div>
      <div className={classes.formSubHeading}>Please enter the details below to download the report.</div>
      <form onSubmit={formik.handleSubmit}>
        <Box my={5}>
          <Grid container>
            <Grid item xs={12}>
              <Box display={(props.isSuperAdmin || props.isViewOnly) ? '' : 'none'}>
                <TextField
                  label="Account Name"
                  select
                  name="account"
                  fullWidth
                  value={formik.values.account}
                  disabled={!(props.isSuperAdmin || props.isViewOnly)}
                  style={{ maxWidth: 500 }}
                  onChange={handleAccountChange}
                  InputProps={{
                    classes: { root: classes.input }
                  }}
                  SelectProps={{
                    MenuProps: {
                      classes: { paper: classes.paper },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                      },
                      getContentAnchorEl: null
                    }
                  }}
                  error={formik.touched.account && Boolean(formik.errors.account)}
                  helperText={formik.touched.account && formik.errors.account}
                >
                  {(props.isSuperAdmin || props.isViewOnly) ? props.accounts.map(account => (
                    <MenuItem value={_.get(account, 'id', '')}>{_.get(account, 'company_name', 'NA')}</MenuItem>
                  )) :
                    <MenuItem value={currentUser.account_id}>{currentUser.account_company_name}</MenuItem>
                  }
                </TextField>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Program Name"
                select
                name="program"
                fullWidth
                value={formik.values.program}
                disabled={!formik.values.account}
                style={{ maxWidth: 500 }}
                onChange={formik.handleChange}
                InputProps={{
                  classes: { root: classes.input }
                }}
                SelectProps={{
                  MenuProps: {
                    classes: { paper: classes.paper },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }
                }}
                error={formik.touched.program && Boolean(formik.errors.program)}
                helperText={formik.touched.program && formik.errors.program}
              >
                {programs.length > 0 ? programs.map(program => (
                  <MenuItem value={program.id}>{program.program_name_admin_ui}</MenuItem>
                )) :
                  <MenuItem value="noProgramFound" disabled>No Program Found</MenuItem>
                }
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                value={formik.values.from_date && moment(formik.values.from_date, 'MM-DD-YYYY').format('MMMM DD, YYYY')}
                fullWidth
                InputProps={{
                  endAdornment: <DateRangeOutlinedIcon style={{ color: "#0000008a" }} />,
                  readOnly: true
                }}
                onClick={() => setFromDate(true)}
                InputLabelProps={{ shrink: formik.values.from_date ? true : undefined }}
                label="From Date"
                style={{ maxWidth: 500 }}
                error={formik.touched.from_date && Boolean(formik.errors.from_date)}
                helperText={formik.touched.from_date && formik.errors.from_date}
              />
              {fromDate &&
                <Box className={classes.dateWrapper}>
                  <CustomDatePicker value={formik.values.from_date} handleCancel={() => setFromDate(false)}
                    handleDone={((data) => { data.date && formik.setFieldValue('from_date', data.date); setFromDate(false) })} />
                </Box>
              }
            </Grid>
            <Grid item xs={12}>
              <TextField
                value={formik.values.to_date && moment(formik.values.to_date, 'MM-DD-YYYY').format('MMMM DD, YYYY')}
                fullWidth
                InputProps={{
                  endAdornment: <DateRangeOutlinedIcon style={{ color: "#0000008a" }} />,
                  readOnly: true
                }}
                onClick={() => setToDate(true)}
                InputLabelProps={{ shrink: formik.values.to_date ? true : undefined }}
                label="To Date"
                style={{ maxWidth: 500 }}
                error={formik.touched.to_date && Boolean(formik.errors.to_date)}
                helperText={formik.touched.to_date && formik.errors.to_date}
              />
              {toDate &&
                <Box className={classes.dateWrapper}>
                  <CustomDatePicker value={formik.values.to_date} handleCancel={() => setToDate(false)}
                    handleDone={((data) => { data.date && formik.setFieldValue('to_date', data.date); setToDate(false) })} />
                </Box>
              }
            </Grid>
          </Grid>
        </Box>
        <div>
          <span className={classes.mr2} ><OutlinedButton type="submit" label="Download CSV" onClick={() => { formik.setFieldValue('report_type', 'CSV') }} /></span>
          <span className={classes.mr2} ><OutlinedButton type="submit" label="Download PDF" onClick={() => { formik.setFieldValue('report_type', 'PDF') }} /></span>
        </div>
      </form>
    </>
  )
}

const mapStateToProps = ({ auth }) => {
  const { currentUser, isSuperAdmin, isViewOnly, timeZone } = auth
  return { currentUser, isSuperAdmin, isViewOnly, timeZone }
}

export default connect(mapStateToProps, {})(EventsSummary)